import React, { useState, useEffect } from 'react';
import { PageTransition } from '../transitions/PageTransition';
import { Santri } from '../../types';
import { PortalGuruBerita } from '../portalguru/PortalGuruBerita';
import { PortalGuruDetailBerita } from '../portalguru/PortalGuruDetailBerita';
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getCategoryColor } from '../portalguru/newsData';
import { Calendar, Repeat } from 'lucide-react';
import { PortalWaliProfil } from './PortalWaliProfil';
import { PortalWaliProfilInfo } from './PortalWaliProfilInfo';
import { PortalWaliBayar } from './PortalWaliBayar';
import { useAuth } from '../../hooks/useAuth';
import { useGuru } from '../../context/GuruContext';
import { useSantri } from '../../context/SantriContext';
import { useUserAccess } from '../../context/UserAccessContext';
import { usePermit } from '../../context/PermitContext';
import { useNavigate } from 'react-router-dom';

export const PortalWaliDashboard: React.FC = () => {
  const { authUser, logout } = useAuth();
  const { loading: permitsLoading } = usePermit();
  const { isSuperAdmin } = useUserAccess();
  const { allGuruRecords } = useGuru();
  const { santri: allSantri } = useSantri();
  const navigate = useNavigate();

const currentGuru = React.useMemo(() => allGuruRecords.find(g => g.email === authUser?.email), [allGuruRecords, authUser]);

    const myChildren = React.useMemo(() => {
      if (isSuperAdmin) {
          return [
              {
                  id: 'dummy-child-1',
                  nama: 'Ahmad Santri',
                  kelas: '3A',
                  nis: '12345678',
                  waliKelas: 'Ust. Ahmad',
                  noKk: 'DUMMY_KK',
                  rfid: 'DUMMY_RFID_1',
                  asrama: 'Asrama Contoh',
                  murobbi: 'Ust. Hasan',
                  status: 'Aktif'
              } as Santri,
              {
                  id: 'dummy-child-2',
                  nama: 'Budi Santri',
                  kelas: '4B',
                  nis: '87654321',
                  waliKelas: 'Ust. Zain',
                  noKk: 'DUMMY_KK',
                  rfid: 'DUMMY_RFID_2',
                  asrama: 'Asrama Putra 2',
                  murobbi: 'Ust. Budi',
                  status: 'Aktif'
              } as Santri
          ];
      }

      if (!currentGuru || !currentGuru.noKk || !allSantri) return [];
      return allSantri.filter(s => s.noKk === currentGuru.noKk);
  }, [currentGuru, allSantri, isSuperAdmin]);

  const [activeChildIndex, setActiveChildIndex] = useState(0);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);

  // Default to first child or selected child
  const santri = myChildren[activeChildIndex] || myChildren[0];
  const currentGuruName = currentGuru?.nama || 'Wali Santri';

  // Hardcoded for now based on the template, but using the specific color #40513B
  const primaryColor = '#40513B';
  const activeColor = '#1B3C53'; // Requested "Beranda" active color

  const [activeTab, setActiveTab] = useState<'home' | 'berita' | 'bayar' | 'profil' | 'log'>('home');
  const [selectedNewsItem, setSelectedNewsItem] = useState<any>(null);
  const [latestNews, setLatestNews] = useState<any[]>([]);
  const [profileView, setProfileView] = useState<string>('main');

  useEffect(() => {
    if (!permitsLoading && authUser) {
      // If the user has no children and isn't super admin, redirect them out
      if (!isSuperAdmin && myChildren.length === 0) {
        navigate('/portal-guru');
      }
    }
  }, [permitsLoading, myChildren, authUser, isSuperAdmin, navigate]);

  useEffect(() => {
    // Fetch latest news for Wali Santri
    const q = query(
      collection(db, 'berita'),
      where('status', '==', 'published')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];

      // Client-side Sort
      items.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return dateB.getTime() - dateA.getTime();
      });

      // Filter Target
      items = items.filter(item => {
          if (!item.target || item.target.length === 0) return true;
          return item.target.includes('Wali Santri');
      });

      // Limit to 3 latest
      setLatestNews(items.slice(0, 3));
    });

    return () => unsubscribe();
  }, []);

  if (!santri) {
      return <div className="min-h-screen flex items-center justify-center">Memuat Data Santri...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f6f8f6] text-[#111811] font-sans antialiased">
      <PageTransition>
        <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden pb-24">

          {activeTab === 'home' && (
            <>
              <div className="bg-[#1B3C53] rounded-b-3xl shadow-sm pb-6">
                <div className="flex items-center justify-between p-4 pt-6">
                  <div>
                    <h2 className="text-xl font-bold tracking-tight text-white">Portal Wali Santri</h2>
                  </div>
                  {((currentGuru && currentGuru.statusKeaktifan !== 'Nonaktif') || isSuperAdmin) && (
                    <button
                      onClick={() => navigate('/portal-guru')}
                      className="flex items-center justify-center size-10 rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                      title="Kembali ke Akun Pegawai"
                    >
                      <Repeat size={24} />
                    </button>
                  )}
                </div>
                <div className="px-4 mt-2">
                  <div className="mb-3 px-1">
                    <p className="text-sm text-white/70 font-medium">Assalamualaikum,</p>
                    <h2 className="text-lg font-bold tracking-tight text-white">{currentGuruName}</h2>
                  </div>
                  <div className="flex flex-col rounded-2xl bg-[#F9F8F6] p-5 text-[#111811] shadow-lg relative overflow-hidden group">
                    <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[#40513B] opacity-10 blur-2xl"></div>
                    <div className="flex items-center gap-4 z-10">
                      <div
                        className="bg-center bg-no-repeat bg-cover rounded-full h-16 w-16 border border-[#1B3C53] shrink-0"
                        style={{backgroundImage: `url("${santri.foto || 'https://placehold.co/150x150'}")`}}
                      >
                      </div>
                      <div className="flex flex-col justify-center flex-1 min-w-0">
                        <h3 className="text-lg font-bold leading-tight truncate pr-2 text-[#111811]">{santri.nama}</h3>
                        <p className="text-[#40513B] text-sm font-medium truncate">
                          {santri.kelas} - {santri.waliKelas ? `Wali Kelas: ${santri.waliKelas}` : 'Belum ada Wali Kelas'}
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">NIS: {santri.nis || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 p-4 -mt-2">
                <div className="flex flex-row items-center justify-between rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700 shrink-0">
                      <span className="material-symbols-outlined text-2xl">account_balance_wallet</span>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs font-medium mb-1">Sisa Saldo</p>
                      <p className="text-[#111811] text-2xl font-bold tracking-tight">Rp 500.000</p>
                    </div>
                  </div>
                  <button className="bg-[#40513B]/10 text-[#40513B] px-4 py-2 rounded-lg text-sm font-bold hover:bg-[#40513B]/20 transition">Top Up</button>
                </div>

                <div className="flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-700 shrink-0">
                        <span className="material-symbols-outlined text-2xl">calendar_month</span>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-medium mb-1">Kehadiran Bulan Ini</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[#111811] text-2xl font-bold tracking-tight">95%</span>
                          <span className="text-[10px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Sangat Baik</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end">
                      <p className="text-xs text-gray-400">Total Hari</p>
                      <p className="text-lg font-bold text-gray-700">19<span className="text-sm text-gray-400 font-normal">/20</span></p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div className="bg-blue-600 h-3 rounded-full relative" style={{width: '95%'}}>
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-1.5 w-1.5 bg-white rounded-full mr-1 opacity-50"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 mb-4">
                <div className="relative overflow-hidden rounded-2xl bg-red-50 border border-red-100 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                      <span className="material-symbols-outlined">notifications_active</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-sm font-bold text-red-900">Tagihan Belum Dibayar</h3>
                          <p className="text-xs text-red-700 mt-1 line-clamp-1">SPP Bulan April 2024</p>
                        </div>
                        <span className="text-sm font-bold text-red-600">Rp 250.000</span>
                      </div>
                      <div className="mt-3 flex gap-3">
                        <button className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-red-700 active:scale-95">Bayar Sekarang</button>
                        <button className="flex-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-bold text-red-700 shadow-sm transition hover:bg-red-50 active:scale-95">Lihat Detail</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-2">
                <h2 className="text-[#111811] text-lg font-bold mb-3 px-1">Menu Utama</h2>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Tahfidz', icon: 'book_2', color: 'bg-emerald-50 text-emerald-600', path: '/portal-wali/tahfidz' },
                    { label: 'Akademik', icon: 'school', color: 'bg-blue-50 text-blue-600', path: '/portal-wali/akademik' },
                    { label: 'Keuangan', icon: 'paid', color: 'bg-cyan-50 text-cyan-600' },
                    { label: 'Kalender Akademik', icon: 'calendar_month', color: 'bg-indigo-50 text-indigo-600', path: '/portal-wali/kaldik-umum' },
                    { label: 'Prestasi', icon: 'emoji_events', color: 'bg-yellow-50 text-yellow-600', path: '/portal-wali/prestasi' },
                    { label: 'Perizinan', icon: 'assignment', color: 'bg-orange-50 text-orange-600', path: '/portal-wali/perizinan' },
                    { label: 'Kesehatan', icon: 'favorite', color: 'bg-red-50 text-red-600' },
                    { label: 'Kedisiplinan', icon: 'gavel', color: 'bg-purple-50 text-purple-600' },
                    { label: 'Kehadiran Kegiatan', icon: 'event_available', color: 'bg-teal-50 text-teal-600' },
                    { label: 'Perkembangan', icon: 'trending_up', color: 'bg-pink-50 text-pink-600' },
                    { label: 'Nilai', icon: 'grade', color: 'bg-lime-50 text-lime-600' },
                    { label: 'Minat dan Bakat', icon: 'palette', color: 'bg-rose-50 text-rose-600' },
                    { label: 'Liburan', icon: 'beach_access', color: 'bg-sky-50 text-sky-600' },
                    { label: 'Mutabaah', icon: 'fact_check', color: 'bg-amber-50 text-amber-600' },
                    { label: 'Berita', icon: 'feed', color: 'bg-blue-50 text-blue-600', action: 'berita' },
                  ].map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                          if (item.path) navigate(item.path);
                          else if (item.action === 'berita') {
                              setSelectedNewsItem(null);
                              setActiveTab('berita');
                          }
                      }}
                      className="flex flex-col items-center justify-center gap-2 rounded-xl bg-white border border-gray-100 p-3 py-4 shadow-sm hover:border-[#40513B]/50 transition active:scale-95"
                    >
                      <div className={`flex h-12 w-12 items-center justify-center rounded-full ${item.color}`}>
                        <span className="material-symbols-outlined text-2xl">{item.icon}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-700 text-center leading-tight">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 px-4 pb-4">
                <div className="flex justify-between items-center mb-3 px-1">
                  <h2 className="text-[#111811] text-lg font-bold">Informasi Terbaru</h2>
                  <a onClick={() => setActiveTab('berita')} className="text-sm font-medium text-[#40513B] hover:underline cursor-pointer">Lihat Semua</a>
                </div>
                <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 snap-x">
                  {latestNews.length > 0 ? (
                    latestNews.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => {
                          setSelectedNewsItem(item);
                          setActiveTab('berita');
                        }}
                        className="min-w-[260px] snap-center rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden flex flex-col cursor-pointer active:scale-95 transition-transform"
                      >
                        <div
                          className="h-32 w-full bg-cover bg-center bg-gray-200"
                          style={{backgroundImage: item.image ? `url("${item.image}")` : 'none'}}
                        >
                           {!item.image && <div className="w-full h-full flex items-center justify-center text-gray-400"><Calendar size={32}/></div>}
                        </div>
                        <div className="p-3 flex flex-col flex-1">
                          <span className={`text-xs font-bold mb-1 ${getCategoryColor(item.category || 'Umum').replace('bg-', 'text-').replace('-100', '-600').replace('px-2 py-0.5 rounded-full', '')}`}>
                            {item.category || 'Umum'}
                          </span>
                          <h3 className="text-sm font-bold text-[#111811] line-clamp-2 leading-tight">{item.title}</h3>
                          <p className="text-xs text-gray-500 mt-2">
                            {item.createdAt ? new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(item.createdAt.toDate()) : ''}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="w-full text-center py-8 text-gray-400 text-sm italic">
                      Belum ada informasi terbaru.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {activeTab === 'berita' && (
             selectedNewsItem ? (
                <PortalGuruDetailBerita
                    newsItem={selectedNewsItem}
                    onBack={() => setSelectedNewsItem(null)}
                />
            ) : (
                <PortalGuruBerita
                    isWali={true}
                    onItemClick={setSelectedNewsItem}
                />
            )
          )}

          {activeTab === 'log' && (
              <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center animate-in fade-in zoom-in duration-300">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
                      <span className="material-symbols-outlined text-gray-400 text-4xl">history</span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Log Aktivitas</h2>
                  <p className="text-sm text-gray-500 max-w-[280px] leading-relaxed">
                      Halaman ini sedang dalam tahap perbaikan dan pengembangan. Silakan kembali lagi nanti.
                  </p>
                  <button
                      onClick={() => setActiveTab('home')}
                      className="mt-8 px-6 py-2.5 bg-[#40513B] text-white rounded-full text-sm font-bold shadow-lg shadow-green-900/20 active:scale-95 transition-transform"
                  >
                      Kembali ke Beranda
                  </button>
              </div>
          )}

          {activeTab === 'bayar' && <PortalWaliBayar santri={santri} />}

          {activeTab === 'profil' && (
              profileView === 'info' ? (
                  <PortalWaliProfilInfo
                      santri={santri}
                      onBack={() => setProfileView('main')}
                  />
              ) : (
                  <PortalWaliProfil
                      santri={santri}
                      onNavigate={(view) => setProfileView(view)}
                      onLogout={() => {
                          logout();
                          navigate('/');
                      }}
                  />
              )
          )}

        </div>
      </PageTransition>

      {/* Floating Action Button & Speed Dial - Ganti Santri (Only visible on Home and if multiple children) */}
      {activeTab === 'home' && myChildren.length > 1 && (
        <div className="fixed bottom-24 right-4 z-40 flex flex-col items-end gap-3">

          {/* Speed Dial Menu items */}
          {isSpeedDialOpen && (
              <div className="flex flex-col gap-2 items-end mb-2 animate-in slide-in-from-bottom-5 duration-200">
                  {myChildren.map((child, index) => (
                      <button
                          key={child.id}
                          onClick={() => {
                              setActiveChildIndex(index);
                              setIsSpeedDialOpen(false);
                          }}
                          className={`flex items-center gap-3 bg-white px-4 py-2 rounded-full shadow-md border ${activeChildIndex === index ? 'border-brand-primary ring-1 ring-brand-primary' : 'border-gray-200'} transition-all active:scale-95`}
                      >
                          <span className={`text-sm font-bold ${activeChildIndex === index ? 'text-brand-primary' : 'text-gray-700'}`}>
                              {child.nama.split(' ')[0]} {/* Short name */}
                          </span>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${activeChildIndex === index ? 'bg-brand-primary' : 'bg-gray-400'}`}>
                              {child.nama.charAt(0)}
                          </div>
                      </button>
                  ))}
              </div>
          )}

          {/* Main FAB */}
          <button
            className="flex items-center gap-2 rounded-full bg-[#40513B] text-white px-4 py-3 shadow-lg shadow-green-500/20 transition-all hover:bg-[#2f3d2b] active:scale-95 relative"
            onClick={() => setIsSpeedDialOpen(!isSpeedDialOpen)}
          >
            {isSpeedDialOpen && (
              <div className="absolute inset-0 bg-white/20 rounded-full blur-sm" />
            )}
            <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: isSpeedDialOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                {isSpeedDialOpen ? 'close' : 'switch_account'}
            </span>
            <span className="font-bold text-sm">Ganti Santri</span>
          </button>

          {/* Backdrop to close when clicking outside */}
          {isSpeedDialOpen && (
              <div
                  className="fixed inset-0 z-[-1] bg-black/5 backdrop-blur-[1px]"
                  onClick={() => setIsSpeedDialOpen(false)}
                  aria-hidden="true"
              />
          )}
        </div>
      )}

      {/* Fixed Bottom Navigation - Outside PageTransition */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 pb-safe pt-2 px-2 sm:px-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-50">
        <div className="flex items-center justify-between h-16 max-w-md mx-auto w-full">
          <button
            onClick={() => { setActiveTab('home'); setSelectedNewsItem(null); }}
            className={`flex flex-col items-center justify-center gap-1 w-14 sm:w-16 transition-colors ${activeTab === 'home' ? `text-[${activeColor}]` : 'text-gray-400 hover:text-[#1B3C53]'}`}
            style={{ color: activeTab === 'home' ? activeColor : undefined }}
          >
            <span className={`material-symbols-outlined ${activeTab === 'home' ? 'filled' : ''}`}>home</span>
            <span className="text-[10px] font-bold">Beranda</span>
          </button>
          <button
            onClick={() => { setActiveTab('log'); setSelectedNewsItem(null); }}
            className={`flex flex-col items-center justify-center gap-1 w-14 sm:w-16 transition-colors ${activeTab === 'log' ? `text-[${activeColor}]` : 'text-gray-400 hover:text-[#1B3C53]'}`}
            style={{ color: activeTab === 'log' ? activeColor : undefined }}
          >
            <span className={`material-symbols-outlined ${activeTab === 'log' ? 'filled' : ''}`}>history</span>
            <span className="text-[10px] font-medium text-center leading-none">Log<br/>Aktivitas</span>
          </button>
          <button
            onClick={() => { setActiveTab('berita'); setSelectedNewsItem(null); }}
            className={`flex flex-col items-center justify-center gap-1 w-14 sm:w-16 transition-colors ${activeTab === 'berita' ? `text-[${activeColor}]` : 'text-gray-400 hover:text-[#1B3C53]'}`}
            style={{ color: activeTab === 'berita' ? activeColor : undefined }}
          >
            <div className="relative">
              <span className={`material-symbols-outlined ${activeTab === 'berita' ? 'filled' : ''}`}>feed</span>
              {/* Notification badge logic can be added here */}
              {/* <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 border border-white"></span> */}
            </div>
            <span className="text-[10px] font-medium">Berita</span>
          </button>
          <button
            onClick={() => { setActiveTab('bayar'); setSelectedNewsItem(null); }}
            className={`flex flex-col items-center justify-center gap-1 w-14 sm:w-16 transition-colors ${activeTab === 'bayar' ? `text-[${activeColor}]` : 'text-gray-400 hover:text-[#1B3C53]'}`}
            style={{ color: activeTab === 'bayar' ? activeColor : undefined }}
          >
            <span className={`material-symbols-outlined ${activeTab === 'bayar' ? 'filled' : ''}`}>payments</span>
            <span className="text-[10px] font-medium">Bayar</span>
          </button>
          <button
            onClick={() => { setActiveTab('profil'); setSelectedNewsItem(null); }}
            className={`flex flex-col items-center justify-center gap-1 w-14 sm:w-16 transition-colors ${activeTab === 'profil' ? `text-[${activeColor}]` : 'text-gray-400 hover:text-[#1B3C53]'}`}
            style={{ color: activeTab === 'profil' ? activeColor : undefined }}
          >
            <span className={`material-symbols-outlined ${activeTab === 'profil' ? 'filled' : ''}`}>person</span>
            <span className="text-[10px] font-medium">Profil</span>
          </button>
        </div>
        <div className="h-4 w-full"></div>
      </div>

      <style>{`
        .material-symbols-outlined.filled {
            font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        }
        .no-scrollbar::-webkit-scrollbar {
            display: none;
        }
        .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
